import { getString } from 'common/ui-string-labels/ui-strings-utils';

/**
 * Produces a user-facing error string that prefers the backend's descriptive
 * message over a generic fallback.
 *
 * ITO/TInvent clients throw modeled Smithy exceptions that carry a descriptive
 * `.message` (e.g. "Order does not exist or is not available for you"). Callers
 * historically discarded this and showed a generic string, so failures gave no
 * indication of what actually went wrong. This helper extracts that message
 * when present and falls back to the generic (localized) string otherwise.
 *
 * @param error The caught error (typed `unknown`, since `catch` binds `unknown`).
 * @param fallbackKey ui-strings key for the generic message to use when the
 *   error carries no usable message.
 * @returns The backend message when available, otherwise the localized fallback.
 */
export function describeError(error: unknown, fallbackKey: string): string {
    const fallback = getString(fallbackKey);
    if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string' && message.trim().length > 0) {
            return message.trim();
        }
    }
    return fallback;
}

/**
 * Reads a non-ok fetch Response's JSON body and throws an Error carrying the
 * backend's descriptive `.message` when present, or an empty-message Error
 * otherwise (so `describeError` falls back to the localized generic string).
 *
 * Consolidates the repeated "read body -> extract message -> throw" pattern
 * used by services that call the Shop backend proxies directly with `fetch`.
 *
 * @param response The non-ok fetch Response to parse.
 * @throws Always throws — either with the backend message or empty.
 */
export async function parseBackendError(response: Response): Promise<never> {
    const body = await response.json().catch(() => ({}));
    const backendMsg = (body as { message?: unknown }).message;
    if (typeof backendMsg === 'string' && backendMsg.trim()) {
        throw new Error(backendMsg);
    }
    throw new Error(); // no backend message — let describeError use the localized fallback
}
